import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  forwardOppositionFinalOrderPdf,
  generateOppositionFinalOrderPresignedUrls,
  getOppositionFinalOrder,
  previewOppositionFinalOrderOutcomeMessage,
  saveOppositionFinalOrder,
  sendOppositionFinalOrderMessage,
  type OppositionFinalOrderItem,
  type OppositionFinalOrderOutcome,
} from "api/opposition";
import { downloadFileFromUrlWithFallback } from "utils/downloadFileFromUrl";

export const OPPOSITION_FINAL_ORDER_FORM_ID =
  "opposition-final-order-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";

const OUTCOME_OPTIONS: { value: OppositionFinalOrderOutcome; label: string }[] = [
  {
    value: "REFUSED_AFTER_OPPOSITION_HEARING",
    label: "Refused after Opposition Hearing",
  },
  {
    value: "ACCEPTED_AFTER_OPPOSITION_HEARING",
    label: "Accepted after Opposition Hearing",
  },
  {
    value: "ACCEPTED_AFTER_NON_APPEARANCE_IN_HEARING",
    label: "Accepted after non-appearance in Opposition Hearing",
  },
  {
    value: "ACCEPTED_AFTER_NON_FILING_OF_EVIDENCE_BY_OPPONENT",
    label: "Accepted after non-filing of Evidence by Opponent",
  },
];

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Row = {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  outcomeOfHearing: OppositionFinalOrderOutcome | null;
  pdfFileKey: string | null;
  pdfFilename: string | null;
  pdfDownloadUrl: string | null;
  pendingFile: File | null;
  localPreviewUrl: string | null;
};

function toRow(m: OppositionFinalOrderItem): Row {
  return {
    id: m.conflictingMarkId,
    conflictingMark: m.conflictingMark,
    applicationNo: m.applicationNo,
    outcomeOfHearing: m.outcomeOfHearing,
    pdfFileKey: m.finalOrderFileKey,
    pdfFilename: m.finalOrderFilename,
    pdfDownloadUrl: m.finalOrderDownloadUrl,
    pendingFile: null,
    localPreviewUrl: null,
  };
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionFinalOrderToClientView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [showForwardConfirm, setShowForwardConfirm] = useState(false);
  const [showMessageConfirm, setShowMessageConfirm] = useState(false);
  const [messagePreview, setMessagePreview] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionFinalOrder(leadId)
      .then((data) => {
        if (cancelled) return;
        setRows((data.items ?? []).map(toRow));
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load Final Order"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(
    () => () => {
      rows.forEach((r) => {
        if (r.localPreviewUrl) URL.revokeObjectURL(r.localPreviewUrl);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const activeRow = rows[activeIdx] ?? null;

  const updateActive = (patch: Partial<Row>) => {
    if (!activeRow) return;
    setRows((prev) =>
      prev.map((r) => (r.id === activeRow.id ? { ...r, ...patch } : r))
    );
  };

  const handleFileChosen = (file: File | null) => {
    if (!activeRow) return;
    if (activeRow.localPreviewUrl) URL.revokeObjectURL(activeRow.localPreviewUrl);
    if (!file) {
      updateActive({ pendingFile: null, localPreviewUrl: null });
      return;
    }
    updateActive({
      pendingFile: file,
      localPreviewUrl: URL.createObjectURL(file),
    });
  };

  const persist = async (): Promise<boolean> => {
    if (!leadId) return false;
    setSaving(true);
    try {
      const uploads = rows
        .filter((r) => r.pendingFile)
        .map((r) => ({ rowId: r.id, file: r.pendingFile! }));
      const keyMap: Record<string, { key: string; filename: string }> = {};
      if (uploads.length > 0) {
        const presigned = await generateOppositionFinalOrderPresignedUrls(
          leadId,
          uploads.map((u) => ({
            filename: u.file.name,
            mimeType: u.file.type || "application/pdf",
          }))
        );
        if (presigned.length !== uploads.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          uploads.map(async (u, i) => {
            const target = presigned[i];
            const res = await fetch(target.url, {
              method: "PUT",
              body: u.file,
              headers: { "Content-Type": u.file.type || "application/pdf" },
            });
            if (!res.ok) throw new Error(`Failed to upload ${u.file.name}`);
            keyMap[u.rowId] = { key: target.key, filename: u.file.name };
          })
        );
      }
      const saved = await saveOppositionFinalOrder(leadId, {
        applications: rows.map((r) => {
          const upload = keyMap[r.id];
          return {
            conflictingMarkId: r.id,
            finalOrderFileKey: upload?.key ?? r.pdfFileKey,
            finalOrderFilename: upload?.filename ?? r.pdfFilename,
            outcomeOfHearing: r.outcomeOfHearing,
          };
        }),
      });
      setRows((saved.items ?? []).map(toRow));
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Final Order"
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const ok = await persist();
    if (ok) {
      toast.success("Final Order saved.");
      if (leadId) onSuccess?.(leadId);
    }
  };

  const handleForwardPdf = async () => {
    if (!leadId || forwarding) return;
    setForwarding(true);
    try {
      const ok = await persist();
      if (!ok) return;
      await forwardOppositionFinalOrderPdf(leadId, {
        conflictingMarkIds: rows
          .filter((r) => r.pdfFileKey)
          .map((r) => r.id),
        sendWhatsApp: true,
        sendEmail: true,
      });
      toast.success("Final Order PDF forwarded to client.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to forward PDF"
      );
    } finally {
      setForwarding(false);
      setShowForwardConfirm(false);
    }
  };

  const idsForActiveOutcome = (): string[] => {
    const outcome = activeRow?.outcomeOfHearing;
    if (!outcome) return [];
    return rows
      .filter((r) => r.outcomeOfHearing === outcome)
      .map((r) => r.id);
  };

  const openSendMessageConfirm = async () => {
    if (!leadId || sendingMessage) return;
    if (!activeRow?.outcomeOfHearing) {
      toast.error("Select an Outcome of Opposition Hearing first.");
      return;
    }
    setSendingMessage(true);
    try {
      const ok = await persist();
      if (!ok) return;
      const ids = idsForActiveOutcome();
      if (ids.length === 0) return;
      const preview = await previewOppositionFinalOrderOutcomeMessage(leadId, {
        conflictingMarkIds: ids,
      });
      setMessagePreview(preview.message);
      setShowMessageConfirm(true);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load message preview"
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendMessage = async () => {
    if (!leadId || sendingMessage) return;
    const ids = idsForActiveOutcome();
    if (ids.length === 0) return;
    setSendingMessage(true);
    try {
      await sendOppositionFinalOrderMessage(leadId, {
        conflictingMarkIds: ids,
      });
      toast.success("Outcome message sent to client.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send message"
      );
    } finally {
      setSendingMessage(false);
      setShowMessageConfirm(false);
      setMessagePreview("");
    }
  };

  const handleDownload = async () => {
    if (!activeRow?.pdfDownloadUrl) return;
    try {
      const result = await downloadFileFromUrlWithFallback(
        activeRow.pdfDownloadUrl,
        activeRow.pdfFilename ?? "final-order.pdf"
      );
      if (result === "local") toast.success("PDF saved.");
      else toast.info("Opened the PDF in a new tab.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  };

  const handlePreview = () => {
    const url = activeRow?.localPreviewUrl ?? activeRow?.pdfDownloadUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (rows.length === 0 || !activeRow) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No conflicting marks for this opposition.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_FINAL_ORDER_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {rows.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {rows.map((r, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(r.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <label className={LABEL_CLASS}>Outcome of Opposition Hearing</label>
        <Select
          value={activeRow.outcomeOfHearing ?? undefined}
          onValueChange={(value) =>
            updateActive({
              outcomeOfHearing: value as OppositionFinalOrderOutcome,
            })
          }
          disabled={saving}
        >
          <SelectTrigger className="h-9 min-h-[44px] md:min-h-0 w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-[12px] leading-5 text-[#222222] data-[placeholder]:text-[#6D6D6D]">
            <SelectValue placeholder="Select an outcome" />
          </SelectTrigger>
          <SelectContent>
            {OUTCOME_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <label className={LABEL_CLASS}>Final Order PDF (from Registry)</label>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2.5">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              handleFileChosen(f);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={saving}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
          >
            {activeRow.pendingFile || activeRow.pdfFilename
              ? "Replace PDF"
              : "Upload PDF"}
          </button>
          {(activeRow.pendingFile || activeRow.pdfDownloadUrl) && (
            <button
              type="button"
              onClick={handlePreview}
              disabled={saving}
              className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
            >
              Preview
            </button>
          )}
          {activeRow.pdfDownloadUrl && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={saving}
              className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
            >
              Download
            </button>
          )}
        </div>
        {(activeRow.pendingFile || activeRow.pdfFilename) && (
          <span className="text-[11px] leading-4 text-[#6D6D6D]">
            {activeRow.pendingFile
              ? `New: ${activeRow.pendingFile.name}`
              : activeRow.pdfFilename}
          </span>
        )}
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-2.5">
        <button
          type="button"
          onClick={() => setShowForwardConfirm(true)}
          disabled={forwarding || saving}
          className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
        >
          {forwarding ? "Forwarding…" : "Forward PDF to Client"}
        </button>
        <button
          type="button"
          onClick={openSendMessageConfirm}
          disabled={sendingMessage || saving || !activeRow.outcomeOfHearing}
          className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-white bg-[#222] rounded-[6px] hover:bg-[#333] disabled:opacity-60"
        >
          {sendingMessage ? "Working…" : "Send Outcome Message"}
        </button>
      </div>

      <ConfirmMessageModal
        open={showForwardConfirm}
        onClose={() => setShowForwardConfirm(false)}
        previewContent="Forward the Final Order PDF(s) to the client over WhatsApp and Email."
        onConfirm={handleForwardPdf}
        disableConfirm={forwarding}
      />
      <ConfirmMessageModal
        open={showMessageConfirm}
        onClose={() => {
          setShowMessageConfirm(false);
          setMessagePreview("");
        }}
        previewContent={messagePreview}
        onConfirm={handleSendMessage}
        disableConfirm={sendingMessage || !messagePreview}
      />
    </form>
  );
}
